import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'markdown',
  standalone: true
})
export class MarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return '';
    }

    let html = this.escapeHtml(value);

    // Convert markdown to HTML

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Inline code: `code`
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Links: [text](url) - with URL sanitization
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => {
      const sanitizedUrl = this.sanitizeUrl(url);
      if (sanitizedUrl) {
        return `<a href="${sanitizedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return text; // Return plain text if URL is unsafe
    });

    // Unordered lists: - item or * item
    html = html.replace(/^[\-\*]\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

    // Ordered lists: 1. item
    html = html.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>');
    // Wrap consecutive <li> that aren't already in <ul> into <ol>
    html = html.replace(
      /(<li>.*<\/li>\n?)(?![^<]*<\/ul>)/g,
      (match, p1, offset, string) => {
        // Check if already wrapped
        const before = string.substring(0, offset);
        if (before.lastIndexOf('<ol>') > before.lastIndexOf('</ol>')) {
          return match;
        }
        return match;
      }
    );

    // Headers: # text, ## text, ### text
    html = html.replace(/^###\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^##\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^#\s+(.+)$/gm, '<h2>$1</h2>');

    // Blockquotes: > text (escaped as &gt; after escapeHtml)
    html = html.replace(/^&gt;\s*(.+)$/gm, '<blockquote>$1</blockquote>');
    // Merge consecutive blockquotes
    html = html.replace(/<\/blockquote>\n?<blockquote>/g, '<br>');

    // Line breaks: double newline = paragraph, single newline = <br>
    html = html.replace(/\n\n+/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');

    // Wrap in paragraph if not already wrapped
    if (!html.startsWith('<')) {
      html = '<p>' + html + '</p>';
    }

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<[huo])/g, '$1');
    html = html.replace(/(<\/[huo]l>|<\/h[234]>)<\/p>/g, '$1');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  private sanitizeUrl(url: string): string | null {
    const allowedProtocols = ['http:', 'https:', 'mailto:'];
    try {
      const parsed = new URL(url, 'https://placeholder.com');
      if (allowedProtocols.includes(parsed.protocol)) {
        return url;
      }
    } catch {
      // Invalid URL, check if it's a relative path
      if (
        url.startsWith('/') ||
        url.startsWith('./') ||
        url.startsWith('../')
      ) {
        return url;
      }
    }
    return null;
  }
}
